const DOMAIN_REGEX =
  /^(?=.{1,253}$)(?!-)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

export type DomainListNormalizationResult = {
  domains: string[];
  invalid: string[];
};

export function normalizeDomain(domain: string): string {
  return domain.trim().toLowerCase().replace(/^@+/, '').replace(/\.+$/, '');
}

export function isValidDomain(domain: string): boolean {
  return DOMAIN_REGEX.test(normalizeDomain(domain));
}

export function extractEmailDomain(email: string): string | null {
  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.lastIndexOf('@');
  if (atIndex <= 0 || atIndex >= trimmed.length - 1) {
    return null;
  }

  const domain = normalizeDomain(trimmed.slice(atIndex + 1));
  return isValidDomain(domain) ? domain : null;
}

export function hasPlusAddressAlias(email: string): boolean {
  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.lastIndexOf('@');
  if (atIndex <= 0 || atIndex >= trimmed.length - 1) {
    return false;
  }

  return trimmed.slice(0, atIndex).includes('+');
}

export function normalizeDomainList(
  inputDomains: string[]
): DomainListNormalizationResult {
  const domains = new Set<string>();
  const invalid = new Set<string>();

  for (const raw of inputDomains) {
    const normalized = normalizeDomain(raw);
    if (!normalized) {
      continue;
    }
    if (!isValidDomain(normalized)) {
      invalid.add(raw.trim());
      continue;
    }
    domains.add(normalized);
  }

  return {
    domains: Array.from(domains),
    invalid: Array.from(invalid),
  };
}

export function domainMatchesRule(domain: string, rule: string): boolean {
  const normalizedDomain = normalizeDomain(domain);
  const normalizedRule = normalizeDomain(rule);
  if (!normalizedDomain || !normalizedRule) {
    return false;
  }
  return (
    normalizedDomain === normalizedRule ||
    normalizedDomain.endsWith(`.${normalizedRule}`)
  );
}

export function getDomainMatchCandidates(domain: string): string[] {
  const normalizedDomain = normalizeDomain(domain);
  if (!isValidDomain(normalizedDomain)) {
    return [];
  }

  const labels = normalizedDomain.split('.');
  const candidates: string[] = [];
  for (let i = 0; i < labels.length; i += 1) {
    const candidate = labels.slice(i).join('.');
    if (candidate.length > 0) {
      candidates.push(candidate);
    }
  }
  return candidates;
}

export function domainMatchesAnyRule(domain: string, rules: string[]): boolean {
  const normalizedDomain = normalizeDomain(domain);
  if (!isValidDomain(normalizedDomain)) {
    return false;
  }

  const normalizedRules = normalizeDomainList(rules).domains;
  return normalizedRules.some((rule) =>
    domainMatchesRule(normalizedDomain, rule)
  );
}

export function isEmailDomainBlocked(args: {
  emailDomain: string;
  blockedDomains: string[];
  allowedDomains: string[];
}): boolean {
  const emailDomain = normalizeDomain(args.emailDomain);
  if (!isValidDomain(emailDomain)) {
    return false;
  }

  if (domainMatchesAnyRule(emailDomain, args.allowedDomains)) {
    return false;
  }

  return domainMatchesAnyRule(emailDomain, args.blockedDomains);
}
